import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';

export const QUOTA_BALANCES_QUERY_KEY = 'quota-balances';

export async function fetchQuotaBalances({
  scopeType,
  scopeId,
  token,
}: {
  scopeType: string;
  scopeId: string;
  token: string;
}): Promise<QuotaBalanceDto[]> {
  const url = new URL(`${import.meta.env.VITE_API_URL}/entitlements/balances`);
  url.searchParams.set('scopeType', scopeType);
  url.searchParams.set('scopeId', scopeId);
  return sendJSONRequest({ url: url.toString(), method: METHOD.GET, token }) as Promise<QuotaBalanceDto[]>;
}

export function useQuotaBalances(scopeType: string | undefined, scopeId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [QUOTA_BALANCES_QUERY_KEY, scopeType, scopeId],
    queryFn: async () => {
      const token = await getToken();
      return fetchQuotaBalances({ scopeType: scopeType!, scopeId: scopeId!, token: token ?? '' });
    },
    enabled: !!scopeType?.trim() && !!scopeId?.trim(),
  });
}

export function invalidateQuotaBalancesForScope(queryClient: QueryClient, scopeType: string, scopeId: string) {
  return queryClient.invalidateQueries({ queryKey: [QUOTA_BALANCES_QUERY_KEY, scopeType, scopeId] });
}

export function invalidateAllQuotaBalances(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: [QUOTA_BALANCES_QUERY_KEY] });
}
